import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  TablePagination,
  IconButton,
  Tooltip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RefreshIcon from "@mui/icons-material/Refresh";

// ⚠️ CONFIRM THIS PATH from your server startup log ("Mapped {...} route").
// Backend controller path is now @Controller('api/leads-submissions').
// If the log shows /api/api/leads-submissions, remove the leading "api/"
// from that decorator (a global 'api' prefix is likely set in main.ts).
const LEADS_ENDPOINT = `${process.env.REACT_APP_SUPERADMIN_APP_API_BASE_URL}/api/leads-submissions`;

const formatDate = (date) =>
  date
    ? new Date(date).toLocaleString("en-US", {
        month: "short",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "-";

export default function LeadsTable({ sidebarOpen = true }) {
  const sidebarWidth = sidebarOpen ? 200 : 100;

  const [leads, setLeads] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("desc"); // desc = newest first
  const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const fetchLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        search: searchQuery.trim(),
        sortOrder,
        page: page + 1, // backend expects 1-indexed page
        limit: rowsPerPage,
      });
      const res = await fetch(`${LEADS_ENDPOINT}?${params.toString()}`);
      const json = await res.json();

      if (!json.success) {
        throw new Error(json.message || "Request failed");
      }

      setLeads(json.data?.submissions || []);
      setTotalItems(json.data?.pagination?.totalItems || 0);
    } catch (err) {
      console.error("Failed to fetch leads:", err);
      setLeads([]);
      setTotalItems(0);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, sortOrder, page, rowsPerPage]);

  useEffect(() => {
    const t = setTimeout(fetchLeads, 500); // debounce search typing
    return () => clearTimeout(t);
  }, [fetchLeads]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, sortOrder]);

  const toggleSort = () => setSortOrder((p) => (p === "desc" ? "asc" : "desc"));
  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4, xl: 4 },
        ml: { xs: "0px", sm: "0px", md: `${sidebarWidth}px`, xl: `${sidebarWidth}px` },
        transition: "margin-left 0.3s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, mt: "1.2%" }}>
        <Typography variant="h6" sx={{ fontFamily: "Outfit", fontWeight: 600, color: "#374151"}}>
          Leads
        </Typography>
        <Typography variant="caption" sx={{ fontFamily: "Outfit", color: "#9ca3af" }}>
          {totalItems} total submissions
        </Typography>
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Tooltip title={sortOrder === "desc" ? "Newest first" : "Oldest first"}>
          <IconButton
            onClick={toggleSort}
            size="small"
            sx={{ border: "1px solid #e5e7eb", borderRadius: 1.5, px: 1.2, gap: 0.5 }}
          >
            {sortOrder === "desc" ? (
              <ArrowDownwardIcon sx={{ fontSize: 16, color: "#9048FB" }} />
            ) : (
              <ArrowUpwardIcon sx={{ fontSize: 16, color: "#9048FB" }} />
            )}
            <Typography sx={{ fontFamily: "Outfit", fontSize: 12, color: "#374151" }}>
              {sortOrder === "desc" ? "Newest" : "Oldest"}
            </Typography>
          </IconButton>
        </Tooltip>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <TextField
            size="small"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{
              width: { xs: "100%", sm: 300 },
              "& .MuiOutlinedInput-root": {
                fontFamily: "Outfit",
                fontSize: 13,
                backgroundColor: "#fff",
                "& fieldset": { borderColor: "#e5e7eb" },
                "&:hover fieldset": { borderColor: "#9048FB" },
                "&.Mui-focused fieldset": { borderColor: "#9048FB" },
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />

          <Tooltip title="Refresh">
            <span>
              <IconButton
                onClick={fetchLeads}
                size="small"
                disabled={loading}
                sx={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 1.5,
                  "&:hover": { borderColor: "#9048FB", backgroundColor: "#f5f0ff" },
                }}
              >
                <RefreshIcon
                  sx={{
                    fontSize: 18,
                    color: "#9048FB",
                    animation: loading ? "spin 0.8s linear infinite" : "none",
                    "@keyframes spin": {
                      "0%": { transform: "rotate(0deg)" },
                      "100%": { transform: "rotate(360deg)" },
                    },
                  }}
                />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #e5e7eb", borderRadius: 2, maxHeight: 650, overflow: "auto" }}>
        <Table stickyHeader sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "5%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>#</TableCell>
              <TableCell sx={{ width: "16%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Name</TableCell>
              <TableCell sx={{ width: "20%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Email</TableCell>
              <TableCell sx={{ width: "18%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Source</TableCell>
              <TableCell sx={{ width: "13%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Phone</TableCell>
              <TableCell sx={{ width: "11%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Meeting</TableCell>
              <TableCell sx={{ width: "9%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Status</TableCell>
              <TableCell sx={{ width: "8%", fontFamily: "Outfit", fontWeight: 600, color: "#374151", backgroundColor: "#f9fafb" }}>Date</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4 }}>
                  <CircularProgress size={22} sx={{ color: "#9048FB" }} />
                </TableCell>
              </TableRow>
            ) : leads.length > 0 ? (
              leads.map((lead, index) => (
                <TableRow key={lead._id} sx={{ "&:hover": { backgroundColor: "#f9fafb" } }}>
                  <TableCell sx={{ fontFamily: "Outfit", color: "#6b7280" }}>{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500, color: "#374151" }}>
                    {lead.firstName || lead.fullName || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", color: "#6b7280" }}>
                    {lead.email || lead.workEmail || "-"}
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", color: "#6b7280", fontSize: 12 }}>{lead.source || "-"}</TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", color: "#6b7280" }}>{lead.phoneNumber || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={lead.meetingBooked ? "Booked" : "Not Booked"}
                      size="small"
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 500,
                        fontSize: 11,
                        backgroundColor: lead.meetingBooked ? "#d1fae5" : "#f3f4f6",
                        color: lead.meetingBooked ? "#065f46" : "#6b7280",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={lead.status === "reached" ? "Reached" : "Not Reached"}
                      size="small"
                      sx={{
                        fontFamily: "Outfit",
                        fontWeight: 500,
                        fontSize: 11,
                        backgroundColor: lead.status === "reached" ? "#d1fae5" : "#fee2e2",
                        color: lead.status === "reached" ? "#065f46" : "#991b1b",
                      }}
                    />
                  </TableCell>
                  <TableCell sx={{ fontFamily: "Outfit", color: "#6b7280", fontSize: 12 }}>
                    {formatDate(lead.formSubmittedAt || lead.submittedAt || lead.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} sx={{ textAlign: "center", py: 4, fontFamily: "Outfit", color: "#9ca3af", fontSize: 13 }}>
                  No leads found{searchQuery ? ` matching "${searchQuery}"` : ""}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={totalItems}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
        sx={{
          borderBottom: "none",
          "& .MuiTablePagination-toolbar": { fontFamily: "Outfit", color: "#374151" },
          "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": { fontFamily: "Outfit", fontSize: 13, color: "#6b7280" },
        }}
      />
    </Box>
  );
}